// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SectionTitleStyles } from "@mcmc/App/Widgets/SectionTitleWidget/SectionTitleWidget";
import File from "@mcmc/XDL/Icons/File";
import { Roboto } from "@mcmc/XDL/Utilities";
import { css } from "styled-components";

const imageHeight = 145;

const wrapper: InjectableCss = {
    css: css`
        padding-bottom: 0;
        margin-bottom: 30px;
    `,
};

const sectionTitle: SectionTitleStyles = {
    title: {
        css: css`
            .title-slash {
                display: none;
            }
        `,
    },
};

const pagesWrapper: InjectableCss = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        margin: 0 -10px;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`
                        justify-content: space-around;
                    `,
                ],
                "max",
            )}
    `,
};

const linkWrapper: InjectableCss = {
    css: css`
        margin-bottom: 20px;
        flex: 0 0 25%;
        max-width: 230px;
        padding: 0 10px;

        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        && {
                            flex: 1 1 100%;
                        }
                    `,
                    null,
                    null,
                    css`
                        flex: 0 0 50%;
                    `,
                ],
                "max",
            )}
    `,
};

const link: LinkPresentationProps = {
    color: "text",
    typographyProps: {
        css: css`
            display: block;
            width: 100%;
            height: 100%;
        `,
    },
    css: css`
        width: 100%;
        height: 100%;
        border: 1px solid ${getColor("common.ui3")};
        transition: box-shadow 0.2s;

        &:hover,
        &:focus {
            outline: none;
            box-shadow: ${getProp("theme.shadows.1")};
        }
    `,
};

const image: LazyImageProps = {
    height: `${imageHeight}px`,
    width: "100%",
    css: css`
        background-color: ${getColor("common.ui3")};
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    `,
};

const noImageWrapper: InjectableCss = {
    css: css`
        background-color: ${getColor("common.ui3")};
        height: ${imageHeight}px;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    `,
};

const noImageIcon: IconPresentationProps = {
    src: File,
    color: "primary.contrast",
    size: 60,
};

const heading: TypographyPresentationProps = {
    ...Roboto,
    size: 14,
    weight: "bold",
    lineHeight: "16px",
    css: css`
        display: block;
        text-align: center;
        padding: 15px;
        letter-spacing: 0.2px;
    `,
};

const styles = {
    wrapper,
    sectionTitle,
    pagesWrapper,
    linkWrapper,
    image,
    noImageWrapper,
    noImageIcon,
    link,
    heading,
};

export default styles;
